import { Alert } from '@mm/components';
import { useUploadActions } from '@mm/state/actions/uploadActions';
import AttributeModel from '@mm/state/model/AttributeModel';
import ButtonModel, { ButtonType } from '@mm/state/model/ButtonModel';
import { FormEntry } from '@mm/state/model/FormsModel';
import WidgetModel, {
  GalleryWidgetConfig,
  ListAndGalleryEntry,
  WidgetBase,
} from '@mm/state/model/WidgetModel';
import classnames from 'classnames';
import { some } from 'lodash';
import React, { useEffect, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { URLWithAttributesValidator } from '../../../../../../../common/helpers/validators/URLWithAttributesValidator';
import { SortableElement } from '../../../../../../../common/lib/react-sortable-hoc';
import { ErrorPortal } from '../../../../../startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { GalleryListItemBody } from './GalleryListItemBody/GalleryListItemBody';
import './ListItem.scss';

export const ListWidgetComponentConstants = {
  COMPACT: 'compact',
  LARGE: 'large',
  CHECKBOX_LABEL: 'large top element style',
  MAX_ITEMS_TITLE: 'Lists may only contain 4 items',
};

type ErrorMessagesHash = {
  url?: string | null;
  item?: string | null;
  title?: string | null;
};

type Props = {
  widget: WidgetBase<GalleryWidgetConfig> & Partial<WidgetModel>;
  item: ListAndGalleryEntry;
  onUpdate: (entry: Partial<FormEntry | ListAndGalleryEntry>) => void;
  onDelete: () => void;
  attributes: AttributeModel[];
  couldShowUploadFormModal: boolean;
  onRatioChange: (isImageSquare?: boolean) => void;
  error: string;
  onOrderButtonClick: (forward?: boolean) => {};
  setUnsavedEntryButton?: (unsavedButton: ButtonModel | undefined) => void;
  disabledButtonTypes?: ButtonType[];
  showAttributePicker?: boolean;
  usedInSlideout?: boolean;
  forInstagram?: boolean;
};

const ListWidgetItem = (props: Props) => {
  const {
    item,
    onUpdate,
    attributes,
    widget,
    couldShowUploadFormModal,
    onRatioChange,
    onDelete,
    error,
    onOrderButtonClick,
    setUnsavedEntryButton,
    disabledButtonTypes,
    showAttributePicker,
    usedInSlideout,
    forInstagram,
  } = props;

  const [prevAssetId, setPrevAssetId] = useState<number | null>(null);
  const [errors, setErrors] = useState<ErrorMessagesHash>({});

  const validateItem = () => {
    const { url, description, file, buttons } = item;
    if (url || description || file || (buttons && buttons.length > 0)) {
      return null;
    }
    return 'Set up at least one more item field: subtitle, button or image';
  };

  const validateTitle = () => {
    const { title } = item;
    return title ? null : 'Title is required';
  };

  const validateUrl = () => {
    const { url } = item;
    if (!url) {
      return null;
    }
    const validator = new URLWithAttributesValidator();
    validator.validate(url);
    return validator.validationMessage || null;
  };

  const [hasAttributesInBody, setHasAttributesInBody] = useState(false);

  useEffect(() => {
    const { title, description } = item;

    if (showAttributePicker) {
      setHasAttributesInBody(false);
      return;
    }

    setHasAttributesInBody(/{{.+}}/.test(title) || /{{.+}}/.test(description));
  }, [item, showAttributePicker]);

  const validationMessages = () => ({
    item: validateItem(),
    title: validateTitle(),
    url: validateUrl(),
  });

  const setupErrors = (messages: ErrorMessagesHash) => {
    // @ts-ignore
    if (some(messages, (message, name) => message !== errors[name])) {
      setErrors({
        ...messages,
      });
    }
  };

  const checkValidation = () => {
    const messagesHash = validationMessages();
    setupErrors(messagesHash);
    return some(messagesHash, message => message !== null);
  };

  const onUpdateItem = (id: number, skipUpdatePreviousAsset?: boolean) => {
    onUpdate({ ...item, assetId: id });
    checkValidation();
    if (!skipUpdatePreviousAsset) {
      setPrevAssetId(id);
    }
  };

  const removeImage = () => onUpdate({ ...item, assetId: null, file: '' });

  const onPersist = () => onUpdate(item);

  const { uploadImage } = useUploadActions();

  const uploadImageHandler = async (
    image: Parameters<typeof uploadImage>[0],
  ) => {
    const { assetId } = item;
    setPrevAssetId(assetId);
    const id = await uploadImage(image);

    onUpdateItem(id, true);
  };

  const onCancelCrop = () => {
    prevAssetId ? onUpdateItem(prevAssetId) : removeImage();
  };

  useEffect(() => {
    checkValidation();
  });

  const tooltipId = `entry-component-${item.id}`;
  const classList = classnames('list-item', {
    'list-item__with-alert': hasAttributesInBody,
  });

  const alert = (
    <div className="gallery-widget__alert-container">
      <Alert
        className="gallery-widget__warning"
        type={'error'}
        showCloseIcon={false}
      >
        Attributes are not available on the first widget since we only get
        contact’s profile AFTER their first response.
      </Alert>
    </div>
  );

  return (
    <div className={classList}>
      <GalleryListItemBody
        disabledButtonTypes={disabledButtonTypes}
        uploadImage={uploadImageHandler}
        removeImage={removeImage}
        onCancelCrop={onCancelCrop}
        onPersist={onPersist}
        onItemUpdated={onUpdate}
        errors={errors}
        widget={widget}
        item={item}
        attributes={attributes}
        onDeleteClick={onDelete}
        tooltipId={tooltipId}
        isValid={!error}
        onRatioChange={onRatioChange}
        couldShowUploadFormModal={couldShowUploadFormModal}
        onOrderButtonClick={onOrderButtonClick}
        setUnsavedEntryButton={setUnsavedEntryButton}
        showAttributePicker={showAttributePicker}
        usedInSlideout={usedInSlideout}
        forInstagram={forInstagram}
      />
      <UncontrolledTooltip
        className="widget-item__tooltip"
        innerClassName="widget-item__inner-tooltip"
        target={tooltipId}
      >
        {error}
      </UncontrolledTooltip>

      {hasAttributesInBody && (
        <ErrorPortal widget={widget}>{alert}</ErrorPortal>
      )}
    </div>
  );
};

type WrappedListWidgetItem = Props & {
  itemRef: HTMLElement;
};

type ListWidgetItemWithRef = Props & {
  ref: HTMLElement;
};

const WrappedListWidgetItem = ({
  itemRef,
  ...props
}: WrappedListWidgetItem) => (
  <>
    {React.createElement<ListWidgetItemWithRef>(ListWidgetItem, {
      ref: itemRef,
      ...props,
    })}
  </>
);

export default SortableElement(WrappedListWidgetItem);
